import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, X } from "lucide-react";
import React, { useCallback, useState } from "react";
import { toast } from "react-toastify";
import { useRestClient } from "../../../core/api/RestClientContext";
import { Button, RefreshBtn } from "../../../core/components/Button";
import { Input, Label, Textarea } from "../../../core/components/FormElements";
import { Modal } from "../../../core/components/Modal";
import { TelegramProxyLink } from "../../../core/models/proxy";
import "./TelegramProxyLinksManager.css";

const TELEGRAM_PROXY_LINKS_QUERY_KEY = ["telegram-proxy-links"];

export const TelegramProxyLinksManager: React.FC = () => {
	const client = useRestClient();
	const queryClient = useQueryClient();
	const [friendlyName, setFriendlyName] = useState("");
	const [url, setUrl] = useState("");
	const [editingLink, setEditingLink] = useState<TelegramProxyLink | null>(null);
	const [modalOpen, setModalOpen] = useState(false);
	const [isSaving, setIsSaving] = useState(false);
	const [deletingId, setDeletingId] = useState<number | null>(null);

	const {
		data: links = [],
		isLoading,
		error,
		refetch,
	} = useQuery<TelegramProxyLink[]>({
		queryKey: TELEGRAM_PROXY_LINKS_QUERY_KEY,
		queryFn: () => client.getTelegramProxyLinks(),
		staleTime: 60_000,
	});

	const resetForm = useCallback(() => {
		setFriendlyName("");
		setUrl("");
		setEditingLink(null);
	}, []);

	const handleOpenAdd = useCallback(() => {
		resetForm();
		setModalOpen(true);
	}, [resetForm]);

	const handleOpenEdit = useCallback((link: TelegramProxyLink) => {
		setEditingLink(link);
		setFriendlyName(link.friendlyName);
		setUrl(link.url);
		setModalOpen(true);
	}, []);

	const handleCloseModal = useCallback(() => {
		resetForm();
		setModalOpen(false);
	}, [resetForm]);

	const invalidateLinks = useCallback(async () => {
		await queryClient.invalidateQueries({ queryKey: TELEGRAM_PROXY_LINKS_QUERY_KEY });
	}, [queryClient]);

	const handleSubmit = useCallback(async () => {
		const normalizedName = friendlyName.trim();
		const normalizedUrl = url.trim();

		if (!normalizedName || !normalizedUrl) {
			toast.warn("Заполните название и ссылку на прокси");
			return;
		}

		try {
			setIsSaving(true);
			if (editingLink) {
				await client.updateTelegramProxyLink(editingLink.id, {
					friendlyName: normalizedName,
					url: normalizedUrl,
				});
				toast.success(`Прокси обновлен: ${normalizedName}`);
			} else {
				await client.createTelegramProxyLink({
					friendlyName: normalizedName,
					url: normalizedUrl,
				});
				toast.success(`Прокси добавлен: ${normalizedName}`);
			}
			handleCloseModal();
			await invalidateLinks();
		} catch (submitError) {
			console.error(submitError);
			toast.error("Не удалось сохранить прокси");
		} finally {
			setIsSaving(false);
		}
	}, [client, editingLink, friendlyName, handleCloseModal, invalidateLinks, url]);

	const handleDelete = useCallback(
		async (link: TelegramProxyLink) => {
			if (!window.confirm(`Удалить прокси "${link.friendlyName}"?`)) return;

			try {
				setDeletingId(link.id);
				await client.deleteTelegramProxyLink(link.id);
				toast.success(`Прокси удален: ${link.friendlyName}`);
				await invalidateLinks();
			} catch (deleteError) {
				console.error(deleteError);
				toast.error("Не удалось удалить прокси");
			} finally {
				setDeletingId(null);
			}
		},
		[client, invalidateLinks],
	);

	const proxyError =
		error instanceof Error ? error.message : "Не удалось загрузить список прокси";

	return (
		<section className="cu-card cu-card--default proxy-page-section">
			<div className="proxy-page-header">
				<div>
					<h2>Telegram proxy</h2>
					<p>Управление ссылками, которые увидят пользователи в приложении</p>
				</div>
				<div className="proxy-page-header-actions">
					<Button variant="primary" icon={<Plus size={16} />} onClick={handleOpenAdd}>
						Добавить
					</Button>
					<RefreshBtn onClick={() => void refetch()} loading={isLoading} showLabel />
				</div>
			</div>

			{error && <div className="proxy-page-error">{proxyError}</div>}

			<div className="proxy-card">
				<div className="proxy-list-header">
					<h3>Сохраненные прокси</h3>
					<span>{links.length}</span>
				</div>
				{!isLoading && links.length === 0 && (
					<div className="proxy-empty">
						Ссылок пока нет. Нажмите «Добавить», чтобы создать первую.
					</div>
				)}
				<div className="proxy-links-list">
					{links.map((link) => (
						<div key={link.id} className="proxy-link-item">
							<div className="proxy-link-meta">
								<div className="proxy-link-name">{link.friendlyName}</div>
								<div className="proxy-link-url">{link.url}</div>
							</div>
							<div className="proxy-link-actions">
								<Button
									variant="ghost"
									size="sm"
									onClick={() => handleOpenEdit(link)}
									disabled={deletingId === link.id}
								>
									Изменить
								</Button>
								<Button
									variant="danger"
									size="sm"
									onClick={() => void handleDelete(link)}
									disabled={deletingId === link.id}
									loading={deletingId === link.id}
								>
									Удалить
								</Button>
							</div>
						</div>
					))}
				</div>
			</div>

			{modalOpen && (
				<Modal onClose={handleCloseModal} className="proxy-modal">
					<div className="proxy-modal-header">
						<h3>{editingLink ? "Редактировать прокси" : "Добавить прокси"}</h3>
						<Button
							variant="ghost"
							size="sm"
							iconOnly
							icon={<X size={16} />}
							onClick={handleCloseModal}
							aria-label="Закрыть"
						/>
					</div>

					<div className="proxy-modal-body">
						<div className="proxy-modal-hint">
							Сохраните готовую ссылку вида <code>tg://proxy?...</code> или{" "}
							<code>https://t.me/proxy?...</code>
						</div>

						<div className="proxy-form-fields">
							<div className="proxy-form-field">
								<Label>Friendly name</Label>
								<Input
									type="text"
									value={friendlyName}
									onChange={(e) => setFriendlyName(e.target.value)}
									placeholder="Например, Основной MTProxy"
									disabled={isSaving}
									autoFocus
								/>
							</div>
							<div className="proxy-form-field">
								<Label>Ссылка</Label>
								<Textarea
									value={url}
									onChange={(e) => setUrl(e.target.value)}
									placeholder="tg://proxy?server=...&#10;или https://t.me/proxy?server=..."
									rows={4}
									disabled={isSaving}
								/>
							</div>
						</div>
					</div>

					<div className="proxy-modal-actions">
						<Button variant="secondary" onClick={handleCloseModal} disabled={isSaving}>
							Отмена
						</Button>
						<Button variant="primary" onClick={() => void handleSubmit()} loading={isSaving}>
							{editingLink ? "Сохранить изменения" : "Добавить прокси"}
						</Button>
					</div>
				</Modal>
			)}
		</section>
	);
};
